// Per-Gaussian BecomingLit shading (envmap branch only).
//
// 1:1 JS port of `shade_per_gaussian` envmap branch in
// /home/shailesh/gaussian-splatting/pbr/becominglit_shading.py:220.
//
// The Python computes:
//   ref_dirs = lightrot @ nrm                       (lightrot=I in our setup → nrm)
//   light_col = env_diffuse(envmap_mip[-1], n)      diffuse-prefiltered cubemap
//   refl = NeuralBSDF(sh, f_dec)                    [(1,3) or broadcast 1→3]
//   diffuse = albedo * refl * light_col
//   wo = normalize(campos - pos)
//   reflvec = normalize(reflect(wo, n))
//   reflvec = lightrot @ reflvec
//   spec_env = env_specular(envmap_mip, fg_lut, reflvec, n, roughness, wo)
//   specular = spec_env * spec_vis
//   color = clamp_min(diffuse, 0) + specular
//
// where env_specular(...) = mip_sample(refl) * (fg_lookup[0] + fg_lookup[1])
// and fg_lookup uses clamped (n·v, roughness).
//


/**
 * Shade N Gaussians and fill `outColors`, `outDiffuse`, `outSpecular`
 * (N×3 each). Albedo is read-only.
 *
 * @param {Object} args
 *   pos:                   Float32Array(N*3)
 *   nrm:                   Float32Array(N*3)  world-space, unit
 *   albedo:                Float32Array(N*3)
 *   roughness:             Float32Array(N)    [0.01, 1.0]
 *   specVis:               Float32Array(N)    [0,1]
 *   campos:                [x, y, z]
 *   neuralBsdfReflectance: Float32Array(N*3)
 *   diffuseEnv(nx,ny,nz, outRGB)               irradiance (Lambertian-convolved env)
 *   specularEnv(rx,ry,rz, roughness, outRGB)   prefiltered specular env
 *   fgLut(nDotV, roughness)  → [A, B]          Karis FG LUT (env_specular sums A+B)
 *   useNeuralBsdf:         boolean
 *   outColors:             Float32Array(N*3)  written
 *   outDiffuse:            Float32Array(N*3)  written (optional but recommended)
 *   outSpecular:           Float32Array(N*3)  written (optional but recommended)
 *   N:                     splat count
 */
export function shadeGaussiansEnvmap({
  pos,
  nrm,
  albedo,
  roughness,
  specVis,
  campos,
  neuralBsdfReflectance,
  diffuseEnv,
  specularEnv,
  fgLut,
  useNeuralBsdf,
  outColors,
  outDiffuse,
  outSpecular,
  N,
}) {
  const cx = campos[0], cy = campos[1], cz = campos[2];
  const diffEnvTmp = new Float32Array(3);
  const specEnvTmp = new Float32Array(3);
  const writeD = !!outDiffuse;
  const writeS = !!outSpecular;

  for (let i = 0; i < N; i++) {
    const i3 = i * 3;
    const px = pos[i3], py = pos[i3 + 1], pz = pos[i3 + 2];
    const nx = nrm[i3], ny = nrm[i3 + 1], nz = nrm[i3 + 2];
    const ar = albedo[i3], ag = albedo[i3 + 1], ab = albedo[i3 + 2];
    const rgh = roughness[i];
    const sv = specVis[i];

    // wo = normalize(campos - pos)
    let wox = cx - px, woy = cy - py, woz = cz - pz;
    const woLen = Math.sqrt(wox * wox + woy * woy + woz * woz);
    if (woLen < 1e-8) {
      outColors[i3] = 0; outColors[i3 + 1] = 0; outColors[i3 + 2] = 0;
      if (writeD) { outDiffuse[i3] = 0; outDiffuse[i3 + 1] = 0; outDiffuse[i3 + 2] = 0; }
      if (writeS) { outSpecular[i3] = 0; outSpecular[i3 + 1] = 0; outSpecular[i3 + 2] = 0; }
      continue;
    }
    wox /= woLen; woy /= woLen; woz /= woLen;

    // ─── Diffuse ─────────────────────────────────────────────────────
    // Python: light_col = env_diffuse(mip[-1], nrm)
    //         diffuse = albedo * refl * light_col
    diffuseEnv(nx, ny, nz, diffEnvTmp);
    let dr, dg, db;

    // NeuralBSDF reflectance modulates albedo on hair; a plain mesh has none,
    // so fall back to identity reflectance (1,1,1) → diffuse = albedo·light_col.
    const rr = useNeuralBsdf ? neuralBsdfReflectance[i3]     : 1;
    const rg = useNeuralBsdf ? neuralBsdfReflectance[i3 + 1] : 1;
    const rb = useNeuralBsdf ? neuralBsdfReflectance[i3 + 2] : 1;
    dr = ar * rr * diffEnvTmp[0];
    dg = ag * rg * diffEnvTmp[1];
    db = ab * rb * diffEnvTmp[2];

    // Python: color = diffuse.clamp_min(0.0) + specular
    if (!(dr > 0)) dr = 0;  // also handles NaN (NaN > 0 is false)
    if (!(dg > 0)) dg = 0;
    if (!(db > 0)) db = 0;

    // ─── Specular ────────────────────────────────────────────────────
    // reflvec = 2(wo·n)n - wo  (Python: reflect = 2*dot(x,n)*n - x)
    const nDotV = nx * wox + ny * woy + nz * woz;
    const reflScale = 2 * nDotV;
    const rfx = reflScale * nx - wox;
    const rfy = reflScale * ny - woy;
    const rfz = reflScale * nz - woz;
    specularEnv(rfx, rfy, rfz, rgh, specEnvTmp);

    // env_specular clamps n·v at 1e-4 before the FG LUT lookup.
    const nvClamped = nDotV < 1e-4 ? 1e-4 : nDotV;
    const fg = fgLut(nvClamped, rgh); // [A, B]
    const ks = (fg[0] + fg[1]) * sv;
    let sr = specEnvTmp[0] * ks;
    let sg = specEnvTmp[1] * ks;
    let sb = specEnvTmp[2] * ks;
    if (!Number.isFinite(sr)) sr = 0;
    if (!Number.isFinite(sg)) sg = 0;
    if (!Number.isFinite(sb)) sb = 0;

    outColors[i3]     = dr + sr;
    outColors[i3 + 1] = dg + sg;
    outColors[i3 + 2] = db + sb;
    if (writeD) { outDiffuse[i3] = dr; outDiffuse[i3 + 1] = dg; outDiffuse[i3 + 2] = db; }
    if (writeS) { outSpecular[i3] = sr; outSpecular[i3 + 1] = sg; outSpecular[i3 + 2] = sb; }
  }
}

/** Build the Karis FG LUT in JS. 256×256 RG Float32Array, indexed by
 *  (nDotV, roughness) ∈ [0,1]². Standard split-sum integration with
 *  GGX importance sampling (Hammersley pattern, 1024 samples per texel).
 *
 *  Mirrors the FG LUT consumed by env_specular in pbr/becominglit_shading.py.
 *  Build once at init; returns a sampler closure that does bilinear lookup. */
export function buildBrdfLut(res = 256, samples = 1024) {
  const lut = new Float32Array(res * res * 2);

  const radicalInverse_VdC = (bits) => {
    bits = (bits << 16) | (bits >>> 16);
    bits = ((bits & 0x55555555) << 1) | ((bits & 0xaaaaaaaa) >>> 1);
    bits = ((bits & 0x33333333) << 2) | ((bits & 0xcccccccc) >>> 2);
    bits = ((bits & 0x0f0f0f0f) << 4) | ((bits & 0xf0f0f0f0) >>> 4);
    bits = ((bits & 0x00ff00ff) << 8) | ((bits & 0xff00ff00) >>> 8);
    return (bits >>> 0) / 0x100000000;
  };

  const importanceSampleGGX = (xi1, xi2, roughness) => {
    const a = roughness * roughness;
    const phi = 2 * Math.PI * xi1;
    const cosTheta = Math.sqrt((1 - xi2) / (1 + (a * a - 1) * xi2));
    const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
    return [Math.cos(phi) * sinTheta, Math.sin(phi) * sinTheta, cosTheta];
  };

  const geometrySchlickGGX = (NdotV, roughness) => {
    const a = roughness;
    const k = (a * a) / 2;
    return NdotV / (NdotV * (1 - k) + k);
  };

  const geometrySmith = (NdotV, NdotL, roughness) => {
    return geometrySchlickGGX(NdotV, roughness) * geometrySchlickGGX(NdotL, roughness);
  };

  for (let y = 0; y < res; y++) {
    const roughness = (y + 0.5) / res;
    for (let x = 0; x < res; x++) {
      const NdotV = Math.max((x + 0.5) / res, 1e-4);
      const Vx = Math.sqrt(1 - NdotV * NdotV);
      const Vy = 0;
      const Vz = NdotV;
      let A = 0, B = 0;
      for (let i = 0; i < samples; i++) {
        const xi1 = i / samples;
        const xi2 = radicalInverse_VdC(i);
        const H = importanceSampleGGX(xi1, xi2, roughness);
        // L = reflect(-V, H) = 2 dot(V, H) H - V
        const VdotH = Vx * H[0] + Vy * H[1] + Vz * H[2];
        const Lx = 2 * VdotH * H[0] - Vx;
        const Ly = 2 * VdotH * H[1] - Vy;
        const Lz = 2 * VdotH * H[2] - Vz;
        const NdotL = Math.max(Lz, 0);
        const NdotH = Math.max(H[2], 0);
        const VdotHp = Math.max(VdotH, 0);
        if (NdotL > 0) {
          const G = geometrySmith(NdotV, NdotL, roughness);
          const G_Vis = (G * VdotHp) / Math.max(NdotH * NdotV, 1e-6);
          const Fc = Math.pow(1 - VdotHp, 5);
          A += (1 - Fc) * G_Vis;
          B += Fc * G_Vis;
        }
      }
      lut[(y * res + x) * 2]     = A / samples;
      lut[(y * res + x) * 2 + 1] = B / samples;
    }
  }
  return { lut, res };
}

/** Bilinear FG LUT sampler. */
export function makeFgLutSampler({ lut, res }) {
  return (nDotV, roughness) => {
    const u = Math.max(0, Math.min(1, nDotV));
    const v = Math.max(0, Math.min(1, roughness));
    const fx = u * (res - 1);
    const fy = v * (res - 1);
    const x0 = Math.floor(fx); const x1 = Math.min(x0 + 1, res - 1);
    const y0 = Math.floor(fy); const y1 = Math.min(y0 + 1, res - 1);
    const tx = fx - x0;
    const ty = fy - y0;
    const i00 = (y0 * res + x0) * 2;
    const i01 = (y0 * res + x1) * 2;
    const i10 = (y1 * res + x0) * 2;
    const i11 = (y1 * res + x1) * 2;
    const w00 = (1 - tx) * (1 - ty);
    const w01 = tx * (1 - ty);
    const w10 = (1 - tx) * ty;
    const w11 = tx * ty;
    const a = w00 * lut[i00]     + w01 * lut[i01]     + w10 * lut[i10]     + w11 * lut[i11];
    const b = w00 * lut[i00 + 1] + w01 * lut[i01 + 1] + w10 * lut[i10 + 1] + w11 * lut[i11 + 1];
    return [a, b];
  };
}
